import { useCallback, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  getCovenantsRequestErrorMessage,
  openOrDownloadBlob,
} from '~/modules/covenants/utils.ts';
import {
  buildExportFileName,
  normalizeContentType,
} from '~/modules/covenants/CovenantsDataForm/CovenantsFormByManager/utils/utils.ts';
import { exportSupervisorApplications } from '~/store/covenants/thunks/supervisorApplicationThunks.ts';
import type { TSupervisorApplicationsRequest } from '~/store/covenants/types.ts';
import type { AppDispatch } from '~/store/store.ts';

type TUseManagerApplicationsExportParams = {
  request: TSupervisorApplicationsRequest;
  onError: (message: string | null) => void;
};

export const useManagerApplicationsExport = ({
  request,
  onError,
}: TUseManagerApplicationsExportParams) => {
  const dispatch = useDispatch<AppDispatch>();
  const [isExportLoading, setIsExportLoading] = useState(false);

  const isDownloadDisabled = useMemo(
    () =>
      !request.fromDate &&
      !request.toDate &&
      !request.startDeadLine &&
      !request.endDealLine,
    [request]
  );

  const handleDownloadReport = useCallback(async () => {
    if (isExportLoading || isDownloadDisabled) {
      return;
    }

    onError(null);
    setIsExportLoading(true);

    try {
      const response = await dispatch(exportSupervisorApplications(request)).unwrap();
      const exportFileName = buildExportFileName(response.filename);

      openOrDownloadBlob({
        blob: response.data,
        fileName: exportFileName,
        fallbackFileName: exportFileName,
        contentType: normalizeContentType(response.contentType),
      });
    } catch (error) {
      onError(getCovenantsRequestErrorMessage(error));
    } finally {
      setIsExportLoading(false);
    }
  }, [dispatch, isDownloadDisabled, isExportLoading, onError, request]);

  return {
    isExportLoading,
    isDownloadDisabled,
    handleDownloadReport,
  };
};
